import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';

import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { User } from '../../utils/types';
import { User as UserIcon, Landmark } from 'lucide-react';
import VerificationModal from '../../components/auth/VerificationModal';
import PhoneVerificationAlert from '../../components/common/PhoneVerificationAlert';
import FacialVerification from '../../components/facial/FacialVerification';

import PageHeader from '../../components/common/PageHeader';
import LoaderOverlay from '../../components/common/LoaderOverlay';
import BankDetailsSection from '../../components/profile/BankDetailsSection';
import PersonalInformationSection from '../../components/profile/PersonalInformationSection';
import AccountSummarySection from '../../components/profile/AccountSummarySection';
import { USER_ROUTES } from '../../constants/routeConstants';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profilePhone, setProfilePhone] = useState(user?.phone || '');
  const [activeTab, setActiveTab] = useState('personal');

  const isSuperAdmin = user?.role === 'super_admin';

  const [isFacialVerificationOpen, setIsFacialVerificationOpen] =
    useState(false);

  const [verificationModal, setVerificationModal] = useState<{
    isOpen: boolean;
    type: 'phone';
    value: string;
  }>({
    isOpen: false,
    type: 'phone',
    value: '',
  });

  const [pageLoading, setPageLoading] = useState(false);

  const handleNavigateToKyc = () => {
    navigate(USER_ROUTES.USER_KYC);
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { isDirty: isPersonalDirty, errors },
  } = useForm<Partial<User>>({
    defaultValues: {
      fullName: user?.fullName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      dob: user?.dob || '',
      address: user?.address || '',
      city: user?.city || '',
      state: user?.state || '',
      country: user?.country || 'IN',
    },
  });

  useEffect(() => {
    const fetchProfileData = async () => {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const userId =
        user?.id ||
        (user as any)?._id ||
        (user as any)?.userId ||
        storedUser?.id ||
        storedUser?._id ||
        storedUser?.userId;

      if (userId) {
        try {
          const freshUser = await authAPI.getProfile(userId);
          updateUser(freshUser);

          setProfilePhone(freshUser.phone || '');
          reset({
            fullName: freshUser.fullName,
            email: freshUser.email,
            phone: freshUser.phone,
            dob: freshUser.dob || '',
            address: freshUser.address || '',
            city: freshUser.city,
            state: freshUser.state,
            country: freshUser.country || 'IN',
          });
        } catch (error: any) {
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            'Check connection';
          toast.error('Sync failed: ' + errorMessage);
        }
      }
    };

    fetchProfileData();
  }, [user?.id, reset, updateUser]);

  const onBankSubmit = async (data: any, bankFile: File | null) => {
    const userId = user?.id || (user as any)?._id || (user as any)?.userId;
    if (!userId) {
      toast.error('User session not found');
      throw new Error('User session not found');
    }

    try {
      const payload = { ...data };
      if (bankFile) {
        payload.verificationDocument = bankFile as any;
      }

      const response = await authAPI.updateBankDetails(userId, payload);
      const updatedBankData = response.data || response;
      updateUser({ bankDetails: updatedBankData });

      toast.success(response.message || 'Bank details updated successfully', {
        id: 'api-success',
      });
    } catch (error: any) {
      console.error('Failed to update bank details:', error);
      const errMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message;
      toast.error(errMsg, { id: 'api-error' });
      throw error;
    }
  };

  const handleAlertVerifyClick = async () => {
    if (!user?.phone) return;
    try {
      const otpResponse = await authAPI.sendPhoneOTP(user.phone);
      if (otpResponse.success) {
        const message =
          otpResponse.message ||
          otpResponse.toastMessage ||
          'OTP sent to your phone successfully';
        toast.success(message);
        setVerificationModal({
          isOpen: true,
          type: 'phone',
          value: user.phone,
        });
      } else {
        const errorMessage =
          otpResponse.message || otpResponse.error || 'Failed to send OTP';
        toast.error(errorMessage);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to send OTP';
      toast.error(errorMessage);
    }
  };

  const handleVerificationSuccess = async () => {
    if (user?.id) {
      try {
        const freshUser = await authAPI.getProfile(user.id);
        updateUser(freshUser);
      } catch (pError: any) {
        const errorMessage =
          pError?.response?.data?.message ||
          pError?.response?.data?.error ||
          pError?.message ||
          'Failed to refresh profile';
        toast.error(errorMessage);
        updateUser({ phoneVerified: true });
      }
    }
    setVerificationModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleVerificationClose = () => {
    setVerificationModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleVerificationModalOpen = async (value: string) => {
    try {
      const otpResponse = await authAPI.sendPhoneOTP(value);
      if (otpResponse.success) {
        const message =
          otpResponse.message ||
          otpResponse.toastMessage ||
          'OTP sent to your phone successfully';
        toast.success(message);
        setVerificationModal({ isOpen: true, type: 'phone', value });
      } else {
        const errorMessage =
          otpResponse.message || otpResponse.error || 'Failed to send OTP';
        toast.error(errorMessage);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to send OTP';
      toast.error(errorMessage);
    }
  };

  const onSubmitPersonal = async (formData: Partial<User>) => {
    if (!user?.id) {
      toast.error('User session not found');
      return;
    }
    setPageLoading(true);
    try {
      const updateData: Partial<User> = {};

      if (formData.fullName?.trim()) {
        updateData.fullName = formData.fullName.trim();
      }
      if (formData.phone?.trim()) {
        updateData.phone = formData.phone.trim();
      }
      if (formData.dob?.trim()) {
        updateData.dob = formData.dob.trim();
      }
      if (formData.address?.trim()) {
        updateData.address = formData.address.trim();
      }
      if (formData.city?.trim()) {
        updateData.city = formData.city.trim();
      }
      if (formData.state?.trim()) {
        updateData.state = formData.state.trim();
      }
      if (formData.country?.trim()) {
        updateData.country = formData.country.trim();
      }

      const response = await authAPI.updateProfile(user.id, updateData);

      const updatedUserData = response.user || response;

      updateUser(updatedUserData);

      reset({
        fullName: updatedUserData.fullName,
        email: updatedUserData.email,
        phone: updatedUserData.phone,
        dob: updatedUserData.dob || '',
        address: updatedUserData.address || '',
        city: updatedUserData.city,
        state: updatedUserData.state,
        country: updatedUserData.country || 'IN',
      });
      toast.success(response.message || 'Profile updated successfully');
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setPageLoading(false);
    }
  };

  const handleFacialVerificationCapture = async (file: File) => {
    setUploadingPhoto(true);
    try {
      const updatedUser = await authAPI.updateFacialVerification(
        user?.id || '',
        file
      );
      updateUser(updatedUser);
      toast.success(
        updatedUser.message || 'Profile photo updated successfully!'
      );
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          'Failed to update photo'
      );
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <LoaderOverlay isLoading={pageLoading} loadingText="Saving changes...">
      <div className="space-y-4 sm:space-y-6">
        <PhoneVerificationAlert onVerifyClick={handleAlertVerifyClick} />
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          <div className="sticky top-0 z-20 bg-background pt-4">
            <PageHeader
              title="Profile"
              description="Manage your personal information and account settings"
            />
            <div className="mt-4">
              <TabsList
                className={`grid w-full ${isSuperAdmin ? 'grid-cols-1' : 'grid-cols-2'} h-11 p-1 bg-muted/60 rounded-xl`}
              >
                <TabsTrigger
                  value="personal"
                  className="inline-flex items-center justify-center gap-2 rounded-lg py-2 text-xs font-semibold ring-offset-background transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                >
                  <UserIcon className="w-4 h-4" />
                  <span>Personal</span>
                </TabsTrigger>
                {!isSuperAdmin && (
                  <TabsTrigger
                    value="bank"
                    className="inline-flex items-center justify-center gap-2 rounded-lg py-2 text-xs font-semibold ring-offset-background transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                  >
                    <Landmark className="w-4 h-4" />
                    <span>Bank Details</span>
                  </TabsTrigger>
                )}
              </TabsList>
            </div>
          </div>

          <TabsContent value="personal">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
              <div className="xl:col-span-2">
                <PersonalInformationSection
                  register={register}
                  handleSubmit={handleSubmit}
                  setValue={setValue}
                  control={control}
                  isPersonalDirty={isPersonalDirty}
                  errors={errors}
                  onSubmitPersonal={onSubmitPersonal}
                  profilePhone={profilePhone}
                  setProfilePhone={setProfilePhone}
                  user={user}
                  setVerificationModal={modal =>
                    handleVerificationModalOpen(modal.value)
                  }
                />
              </div>
              <div className="space-y-6">
                <AccountSummarySection
                  user={user}
                  uploadingPhoto={uploadingPhoto}
                  setIsFacialVerificationOpen={setIsFacialVerificationOpen}
                />
              </div>
            </div>
          </TabsContent>

          {!isSuperAdmin && (
            <TabsContent value="bank">
              <BankDetailsSection
                user={user}
                onSubmit={onBankSubmit}
                pageLoading={pageLoading}
                setPageLoading={setPageLoading}
                onNavigateToKyc={handleNavigateToKyc}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
      <FacialVerification
        isOpen={isFacialVerificationOpen}
        onClose={() => setIsFacialVerificationOpen(false)}
        onCapture={handleFacialVerificationCapture}
        title="Update Profile Photo"
        videoHeight="h-80"
      />

      <VerificationModal
        isOpen={verificationModal.isOpen}
        type={verificationModal.type}
        value={verificationModal.value}
        onSuccess={handleVerificationSuccess}
        onClose={handleVerificationClose}
        otpAlreadySent={true}
      />
    </LoaderOverlay>
  );
};

export default Profile;
